import React, { useEffect, useState } from 'react';
import { CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';

const QuickBooksOAuthResult = () => {
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Processing QuickBooks authorization...');

  useEffect(() => {
    handleOAuthResult();
  }, []);

  const handleOAuthResult = async () => {
    try {
      // Get URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const success = urlParams.get('success') === 'true';
      const sid = urlParams.get('sid') || urlParams.get('sessionId'); // Fallback for old format
      const error = urlParams.get('error');
      const franchiseeId = urlParams.get('franchiseeId');

      // Handle error case
      if (!success && error) {
        setStatus('error');
        setMessage(`OAuth error: ${decodeURIComponent(error)}`);
        handleCompletion(false, sid, franchiseeId);
        return;
      }

      if (!sid) {
        setStatus('error');
        setMessage('Missing session ID');
        handleCompletion(false, sid, franchiseeId);
        return;
      }

      // Success case - we're back on localhost from ngrok callback
      if (success) {
        setStatus('success');
        setMessage('QuickBooks successfully connected!');
        handleCompletion(true, sid, franchiseeId);
      } else {
        setStatus('error');
        setMessage('OAuth was not successful');
        handleCompletion(false, sid, franchiseeId);
      }

    } catch (error) {
      console.error('Error handling OAuth result:', error);
      setStatus('error');
      setMessage(`Error: ${error.message || 'Unknown error occurred'}`);
      handleCompletion(false, null, null);
    }
  };

  const handleCompletion = (success, sid, franchiseeId) => {
    // Debug logging
    console.log('🔍 Popup completion debug:', {
      hostname: window.location.hostname,
      hasOpener: !!window.opener,
      openerClosed: window.opener?.closed,
      windowName: window.name,
      success,
      sid
    });
    
    try {
      // Path 1: BroadcastChannel (works for same-origin tabs/popups)
      try {
        const channel = new BroadcastChannel('qb-oauth');
        channel.postMessage({
          type: 'QB_OAUTH_COMPLETE',
          success,
          sid,
          franchiseeId,
          message
        });
        channel.close();
        console.log('✅ Sent BroadcastChannel message');
      } catch (broadcastError) {
        console.warn('BroadcastChannel failed:', broadcastError);
      }

      // Path 2: postMessage (requires opener, may fail after redirects)
      if (window.opener && !window.opener.closed) {
        try {
          window.opener.postMessage({
            type: 'QB_OAUTH_COMPLETE',
            success,
            sid,
            franchiseeId,
            message
          }, window.location.origin);
          console.log('✅ Sent postMessage to parent');
        } catch (postMessageError) {
          console.warn('postMessage failed:', postMessageError);
        }
      } else {
        console.log('⚠️ No valid opener window for postMessage');
      }

      // Close popup after short delay
      setTimeout(() => {
        console.log('🔒 Closing popup window');
        window.close();
      }, success ? 2000 : 3000);

    } catch (error) {
      console.error('Error in completion handling:', error);
      // Fallback: close after delay
      setTimeout(() => {
        window.close();
      }, 3000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            QuickBooks Integration
          </h2>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-center space-y-4">
            {status === 'processing' && (
              <>
                <RefreshCw className="mx-auto animate-spin text-pest-blue" size={48} />
                <p className="text-gray-700">{message}</p>
              </>
            )}
            
            {status === 'success' && (
              <>
                <CheckCircle className="mx-auto text-green-500" size={48} />
                <p className="text-green-700 font-semibold">{message}</p>
                <p className="text-sm text-gray-500">
                  {window.opener ? 'This window will close automatically...' : 'Redirecting to user management...'}
                </p>
              </>
            )}
            
            {status === 'error' && (
              <>
                <AlertCircle className="mx-auto text-red-500" size={48} />
                <p className="text-red-700 font-semibold">Connection Failed</p>
                <p className="text-gray-600">{message}</p>
                {!window.opener && (
                  <button
                    onClick={() => window.location.href = '/user-management'}
                    className="mt-4 bg-pest-blue hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
                  >
                    Back to User Management
                  </button>
                )}
                {window.opener && (
                  <button
                    onClick={() => window.close()}
                    className="mt-4 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
                  >
                    Close Window
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        <div className="text-center">
          <p className="text-sm text-gray-500">
            QuickBooks OAuth processing complete
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuickBooksOAuthResult;